/**
 * Storage Cleanup Utility
 *
 * Handles deletion of Firebase Storage files for media items.
 * Covers images[], documentUrls[], mediaUrl (video), and audioUrl (audio).
 * Tracks failures and preserves external URLs.
 */

import {
  getStorage,
  storageRef,
  deleteObject,
} from "@/services/legacyBrowserPersistenceBoundary";

/**
 * Check if URL is a Firebase Storage URL
 * @param {string} url - URL to check
 * @returns {boolean} - True if Firebase Storage URL
 */
function isFirebaseStorageUrl(url) {
  if (!url || typeof url !== "string") return false;
  return (
    url.includes("firebasestorage.googleapis.com") ||
    url.includes("firebase.googleapis.com")
  );
}

/**
 * Extract storage path from Firebase Storage URL
 * @param {string} url - Firebase Storage URL
 * @returns {string|null} - Storage path or null if extraction fails
 */
function extractStoragePath(url) {
  try {
    // Handle different Firebase Storage URL formats
    const decodedUrl = decodeURIComponent(url);

    // Format: https://firebasestorage.googleapis.com/v0/b/[bucket]/o/[encoded-path]?alt=media&token=[token]
    const pathMatch = decodedUrl.match(/\/o\/([^?]+)/);
    if (pathMatch && pathMatch[1]) {
      return decodeURIComponent(pathMatch[1]);
    }

    return null;
  } catch (error) {
    console.warn(
      "[StorageCleanup] Failed to extract path from URL:",
      url,
      error
    );
    return null;
  }
}

/**
 * Delete a single file from Firebase Storage
 * @param {string} url - File URL to delete
 * @returns {Promise<{success: boolean, url: string, error?: string}>}
 */
async function deleteStorageFile(url) {
  try {
    // Skip non-Firebase URLs (external links)
    if (!isFirebaseStorageUrl(url)) {
      console.log("[StorageCleanup] Skipping external URL:", url);
      return { success: true, url, skipped: true };
    }

    const path = extractStoragePath(url);
    if (!path) {
      return {
        success: false,
        url,
        error: "Could not extract storage path from URL",
      };
    }

    const storage = getStorage();
    const fileRef = storageRef(storage, path);

    await deleteObject(fileRef);

    console.log("[StorageCleanup] ✅ Deleted:", path);
    return { success: true, url };
  } catch (error) {
    // File not found is considered success (already deleted)
    if (error.code === "storage/object-not-found") {
      console.log("[StorageCleanup] ✅ File already deleted:", url);
      return { success: true, url };
    }

    console.error("[StorageCleanup] ❌ Failed to delete:", url, error);
    return {
      success: false,
      url,
      error: error.message || String(error),
    };
  }
}

/**
 * Delete multiple storage files with failure tracking
 * @param {Array} urls - Array of URLs to delete
 * @returns {Promise<{successes: Array, failures: Array, skipped: Array}>}
 */
async function deleteMultipleFiles(urls) {
  if (!Array.isArray(urls) || urls.length === 0) {
    return { successes: [], failures: [], skipped: [] };
  }

  const results = await Promise.all(urls.map((url) => deleteStorageFile(url)));

  const successes = results
    .filter((r) => r.success && !r.skipped)
    .map((r) => r.url);
  const failures = results.filter((r) => !r.success);
  const skipped = results
    .filter((r) => r.success && r.skipped)
    .map((r) => r.url);

  return { successes, failures, skipped };
}

function collectVariantUrls(value, target) {
  if (Array.isArray(value)) {
    value.forEach((entry) => collectVariantUrls(entry, target));
    return;
  }
  if (!value || typeof value !== "object") return;
  Object.values(value).forEach((url) => {
    if (typeof url === "string" && url) target.push(url);
  });
}

/**
 * Delete all storage files associated with a media document
 * @param {Object} mediaDoc - Media document data
 * @returns {Promise<{totalDeleted: number, totalFailed: number, failures: Array}>}
 */
export async function deleteStorageFiles(mediaDoc) {
  console.log(
    "[StorageCleanup] Processing media document:",
    mediaDoc.id || "unknown"
  );

  const urlsToDelete = [];

  // Collect images[] URLs
  if (Array.isArray(mediaDoc.images)) {
    urlsToDelete.push(...mediaDoc.images.filter(Boolean));
  }
  if (Array.isArray(mediaDoc.mediaThumbnails)) {
    urlsToDelete.push(...mediaDoc.mediaThumbnails.filter(Boolean));
  }
  if (Array.isArray(mediaDoc.originalMediaThumbnails)) {
    urlsToDelete.push(...mediaDoc.originalMediaThumbnails.filter(Boolean));
  }
  if (Array.isArray(mediaDoc.originalVideoUrls)) {
    urlsToDelete.push(...mediaDoc.originalVideoUrls.filter(Boolean));
  }

  collectVariantUrls(mediaDoc.variants, urlsToDelete);
  collectVariantUrls(mediaDoc.imageVariants, urlsToDelete);
  collectVariantUrls(mediaDoc.mediaVariants, urlsToDelete);
  collectVariantUrls(mediaDoc.mediaThumbnailVariants, urlsToDelete);

  // Collect documentUrls[] URLs
  if (Array.isArray(mediaDoc.documentUrls)) {
    urlsToDelete.push(...mediaDoc.documentUrls.filter(Boolean));
  }

  // Collect video mediaUrl
  if (mediaDoc.mediaType === "video" && mediaDoc.mediaUrl) {
    urlsToDelete.push(mediaDoc.mediaUrl);
  }

  // Collect audio audioUrl
  if (mediaDoc.mediaType === "audio" && mediaDoc.audioUrl) {
    urlsToDelete.push(mediaDoc.audioUrl);
  }

  // Also check for other video URL fields
  const videoFields = ["videoUrl", "processedVideoUrl", "originalVideoUrl"];
  videoFields.forEach((field) => {
    if (mediaDoc[field]) {
      urlsToDelete.push(mediaDoc[field]);
    }
  });

  // Remove duplicates
  const uniqueUrls = [...new Set(urlsToDelete)];

  console.log("[StorageCleanup] Found URLs to delete:", {
    total: uniqueUrls.length,
    images: mediaDoc.images?.length || 0,
    documents: mediaDoc.documentUrls?.length || 0,
    hasVideo: !!mediaDoc.mediaUrl,
    hasAudio: !!mediaDoc.audioUrl,
  });

  if (uniqueUrls.length === 0) {
    console.log("[StorageCleanup] No files to delete");
    return { totalDeleted: 0, totalFailed: 0, failures: [] };
  }

  // Delete all files
  const { successes, failures, skipped } = await deleteMultipleFiles(
    uniqueUrls
  );

  // Log results
  console.log("[StorageCleanup] Cleanup complete:", {
    deleted: successes.length,
    failed: failures.length,
    skipped: skipped.length,
  });

  if (failures.length > 0) {
    console.error("[StorageCleanup] Failed deletions:", failures);
  }

  return {
    totalDeleted: successes.length,
    totalFailed: failures.length,
    failures: failures,
  };
}

/**
 * Clean up storage files for a specific media type change
 * Useful when mediaType changes and old files need cleanup
 * @param {Object} oldDoc - Previous document state
 * @param {Object} newDoc - New document state
 * @returns {Promise<Object>} - Cleanup results
 */
export async function cleanupChangedFiles(oldDoc, newDoc) {
  console.log("[StorageCleanup] Cleaning up changed files");

  // Find URLs that were removed
  const oldUrls = new Set();
  const newUrls = new Set();
  const oldVariantUrls = [];
  const newVariantUrls = [];

  // Collect old URLs
  if (Array.isArray(oldDoc.images)) {
    oldDoc.images.forEach((url) => oldUrls.add(url));
  }
  if (Array.isArray(oldDoc.documentUrls)) {
    oldDoc.documentUrls.forEach((url) => oldUrls.add(url));
  }
  if (oldDoc.mediaUrl) oldUrls.add(oldDoc.mediaUrl);
  if (oldDoc.audioUrl) oldUrls.add(oldDoc.audioUrl);
  if (Array.isArray(oldDoc.mediaThumbnails)) {
    oldDoc.mediaThumbnails.forEach((url) => oldUrls.add(url));
  }
  if (Array.isArray(oldDoc.originalMediaThumbnails)) {
    oldDoc.originalMediaThumbnails.forEach((url) => oldUrls.add(url));
  }
  if (Array.isArray(oldDoc.originalVideoUrls)) {
    oldDoc.originalVideoUrls.forEach((url) => oldUrls.add(url));
  }
  collectVariantUrls(oldDoc.variants, oldVariantUrls);
  collectVariantUrls(oldDoc.imageVariants, oldVariantUrls);
  collectVariantUrls(oldDoc.mediaVariants, oldVariantUrls);
  collectVariantUrls(oldDoc.mediaThumbnailVariants, oldVariantUrls);
  oldVariantUrls.forEach((url) => oldUrls.add(url));

  // Collect new URLs
  if (Array.isArray(newDoc.images)) {
    newDoc.images.forEach((url) => newUrls.add(url));
  }
  if (Array.isArray(newDoc.documentUrls)) {
    newDoc.documentUrls.forEach((url) => newUrls.add(url));
  }
  if (newDoc.mediaUrl) newUrls.add(newDoc.mediaUrl);
  if (newDoc.audioUrl) newUrls.add(newDoc.audioUrl);
  if (Array.isArray(newDoc.mediaThumbnails)) {
    newDoc.mediaThumbnails.forEach((url) => newUrls.add(url));
  }
  if (Array.isArray(newDoc.originalMediaThumbnails)) {
    newDoc.originalMediaThumbnails.forEach((url) => newUrls.add(url));
  }
  if (Array.isArray(newDoc.originalVideoUrls)) {
    newDoc.originalVideoUrls.forEach((url) => newUrls.add(url));
  }
  collectVariantUrls(newDoc.variants, newVariantUrls);
  collectVariantUrls(newDoc.imageVariants, newVariantUrls);
  collectVariantUrls(newDoc.mediaVariants, newVariantUrls);
  collectVariantUrls(newDoc.mediaThumbnailVariants, newVariantUrls);
  newVariantUrls.forEach((url) => newUrls.add(url));

  // Find URLs to delete (in old but not in new)
  const urlsToDelete = [...oldUrls].filter((url) => !newUrls.has(url));

  if (urlsToDelete.length === 0) {
    console.log("[StorageCleanup] No files to clean up");
    return { totalDeleted: 0, totalFailed: 0, failures: [] };
  }

  console.log(
    "[StorageCleanup] Cleaning up removed files:",
    urlsToDelete.length
  );

  const { successes, failures } = await deleteMultipleFiles(urlsToDelete);

  return {
    totalDeleted: successes.length,
    totalFailed: failures.length,
    failures: failures,
  };
}
